import * as React from "react";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  CircularProgress,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";

type BrandOption = { id: number; name: string; provider?: string };

const nameCache: Record<string, string> = {};
const inflight: Record<string, Promise<void>> = {};

const brandName = (b: any): string => b?.display_name_override || b?.display_name || "";

const loadBrand = (id: string): Promise<void> => {
  if (!inflight[id]) {
    inflight[id] = axios
      .get(`${ENDPOINTS.BRAND_CATALOG}/${id}`, getConfigGet({}))
      .then((res: any) => {
        nameCache[id] = brandName(res.data?.data);
      })
      .catch(() => {
        nameCache[id] = "";
      });
  }
  return inflight[id];
};

export const brandLabel = (names: Record<string, string>, id: number | string | null | undefined): string => {
  if (id == null || id === "") return "—";
  const name = names[String(id)];
  return name ? `${name} (${id})` : String(id);
};

export const useBrandNames = (ids: (number | string | null | undefined)[]): Record<string, string> => {
  const [names, setNames] = useState<Record<string, string>>({ ...nameCache });
  const key = Array.from(new Set(ids.filter((i) => i != null && i !== "").map(String))).sort().join(",");

  useEffect(() => {
    if (!key) return;
    let live = true;
    const missing = key.split(",").filter((id) => !(id in nameCache));
    Promise.all(missing.map(loadBrand)).then(() => {
      if (live) setNames({ ...nameCache });
    });
    return () => {
      live = false;
    };
  }, [key]);

  return names;
};

export const BrandPicker = ({
  value,
  onChange,
  label = "Brand",
  required,
  className,
}: {
  value: string;
  onChange: (id: string) => void;
  label?: string;
  required?: boolean;
  className?: string;
}) => {
  const anchor = useRef<HTMLDivElement>(null);
  const names = useBrandNames([value]);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<BrandOption[]>([]);

  useEffect(() => {
    if (!open) return;
    let live = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const params: any = { pageSize: 20, sortBy: "display_name", sortOrder: "ASC" };
        if (text.trim()) params.search = text.trim();
        const res: any = await axios.get(ENDPOINTS.BRAND_CATALOG, getConfigGet(params));
        if (!live) return;
        const rows: any[] = res.data?.data || [];
        rows.forEach((b) => {
          nameCache[String(b.id)] = brandName(b);
        });
        setOptions(rows.map((b) => ({ id: b.id, name: brandName(b), provider: b.provider_name })));
      } catch (err) {
        if (live) setOptions([]);
      } finally {
        if (live) setLoading(false);
      }
    }, 300);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [text, open]);

  const pick = (o: BrandOption) => {
    onChange(String(o.id));
    setText("");
    setOpen(false);
  };

  const shown = open ? text : value ? brandLabel(names, value) : "";

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <div ref={anchor} className={className}>
        <TextField
          label={label}
          variant="outlined"
          size="small"
          fullWidth
          required={required}
          value={shown}
          placeholder="Search brands..."
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {loading && open ? (
                  <CircularProgress size={16} />
                ) : value ? (
                  <IconButton size="small" aria-label="clear brand" onClick={() => onChange("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </InputAdornment>
            ),
          }}
        />
        <Popper
          open={open}
          anchorEl={anchor.current}
          placement="bottom-start"
          style={{ zIndex: 1400, width: anchor.current?.offsetWidth }}
        >
          <Paper style={{ maxHeight: 320, overflowY: "auto", marginTop: 4 }}>
            {!loading && options.length === 0 && (
              <Typography variant="body2" color="textSecondary" style={{ padding: "8px 16px" }}>
                No brands found
              </Typography>
            )}
            {options.map((o) => (
              <MenuItem key={o.id} dense selected={String(o.id) === value} onClick={() => pick(o)}>
                {o.name} ({o.id}){o.provider ? ` · ${o.provider}` : ""}
              </MenuItem>
            ))}
          </Paper>
        </Popper>
      </div>
    </ClickAwayListener>
  );
};
